/**
 * Cross-origin attributes for third-party script tags.
 *
 * Only set crossOrigin when the vendor CDN responds with Access-Control-Allow-Origin.
 * Widget/chat SDKs (Cuoral, etc.) usually do **not** — adding crossOrigin
 * causes the browser to block the script (empty onerror `{}`).
 *
 * App bundles: use `crossOrigin: "anonymous"` in next.config.mjs (Next/_next chunks).
 *
 * Owner: all dynamic and next/script third-party loaders should use this module.
 */

export const THIRD_PARTY_SCRIPT_CROSS_ORIGIN = "anonymous" as const;

export type ThirdPartyScriptCrossOrigin =
  typeof THIRD_PARTY_SCRIPT_CROSS_ORIGIN;

export type ThirdPartyScriptVendor =
  | "meta-pixel"
  | "sendpulse"
  | "cuoral"
  | "tawk";

/**
 * Vendors verified to send CORS headers for script loads.
 * Default false — opt in only after checking response headers.
 */
const VENDOR_SUPPORTS_SCRIPT_CROSS_ORIGIN: Record<
  ThirdPartyScriptVendor,
  boolean
> = {
  "meta-pixel": false,
  sendpulse: false,
  cuoral: false,
  tawk: false,
};

/** For next/script `crossOrigin` prop — undefined when vendor has no CORS support. */
export function thirdPartyScriptCrossOriginProp(
  vendor: ThirdPartyScriptVendor,
): ThirdPartyScriptCrossOrigin | undefined {
  return VENDOR_SUPPORTS_SCRIPT_CROSS_ORIGIN[vendor]
    ? THIRD_PARTY_SCRIPT_CROSS_ORIGIN
    : undefined;
}

/** Apply to scripts created with document.createElement("script"). */
export function configureThirdPartyScript(
  script: HTMLScriptElement,
  vendor: ThirdPartyScriptVendor,
): void {
  if (!VENDOR_SUPPORTS_SCRIPT_CROSS_ORIGIN[vendor]) {
    return;
  }
  script.crossOrigin = THIRD_PARTY_SCRIPT_CROSS_ORIGIN;
}

/** @deprecated Use configureThirdPartyScript(script, vendor) */
export function applyThirdPartyScriptCrossOrigin(
  script: HTMLScriptElement,
): void {
  script.crossOrigin = THIRD_PARTY_SCRIPT_CROSS_ORIGIN;
}
